import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Draggable } from "gsap/Draggable";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger, Draggable);

document.addEventListener('DOMContentLoaded', () => {

	// Force autoplay for all videos on mobile (iOS fix)
	// Exclude HLS videos (.m3u8) as they're handled by InitVideo.js
	const allAutoplayVideos = document.querySelectorAll('video[autoplay], video[data-autoplay-on-view]');
	const autoplayVideos = Array.from(allAutoplayVideos).filter(video => {
		const src = video.dataset.src || video.querySelector('source')?.src || video.src;
		return src && !src.includes('.m3u8');
	});

	if (autoplayVideos.length > 0) {
		let hasUserInteracted = false;

		const playVideo = (video) => {
			if (video.paused) {
				// Ensure critical attributes for iOS autoplay
				video.muted = true;
				video.playsInline = true;
				video.setAttribute('playsinline', '');
				video.setAttribute('webkit-playsinline', '');
				video.setAttribute('muted', '');

				const playPromise = video.play();

				if (playPromise !== undefined) {
					playPromise.then(() => {
						// Video started playing successfully
					}).catch(err => {
						console.log('Video autoplay blocked, waiting for user interaction', err);
					});
				}
			}
		};

		const playAllVideos = () => {
			autoplayVideos.forEach((video) => {
				// Check if video is in viewport
				const rect = video.getBoundingClientRect();
				const isInViewport = (
					rect.top >= 0 &&
					rect.left >= 0 &&
					rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
					rect.right <= (window.innerWidth || document.documentElement.clientWidth)
				);

				if (isInViewport || video.hasAttribute('autoplay')) {
					playVideo(video);
				}
			});
		};

		// Intersection Observer for playing videos when they come into view
		const videoObserverOptions = {
			threshold: 0.1,
			rootMargin: '100px'
		};

		const videoObserverCallback = (entries) => {
			entries.forEach((entry) => {
				const video = entry.target;

				if (entry.isIntersecting) {
					playVideo(video);
				} else {
					// Optionally pause when out of view to save resources
					// video.pause();
				}
			});
		};

		const videoObserver = new IntersectionObserver(videoObserverCallback, videoObserverOptions);

		autoplayVideos.forEach((video) => {
			// Ensure critical attributes for iOS
			video.muted = true;
			video.playsInline = true;
			video.setAttribute('playsinline', '');
			video.setAttribute('webkit-playsinline', '');

			videoObserver.observe(video);

			// Try to play immediately
			playVideo(video);
		});

		// iOS requires user interaction before video can play
		// Listen for first touch/click and play all videos
		const enableAutoplay = () => {
			if (!hasUserInteracted) {
				hasUserInteracted = true;
				playAllVideos();
			}
		};

		// Listen for various user interaction events - more aggressive for iOS Chrome
		const events = ['touchstart', 'touchend', 'touchmove', 'click', 'scroll'];
		events.forEach(eventType => {
			document.addEventListener(eventType, enableAutoplay, { once: true, passive: true });
		});

		// Try multiple times with increasing delays for stubborn iOS Chrome
		setTimeout(playAllVideos, 100);
		setTimeout(playAllVideos, 500);
		setTimeout(playAllVideos, 1000);
	}


	// Typing animation for hero text
	const typingElement = document.querySelector('.typing-text');

	if (typingElement) {
		const textA = typingElement.dataset.textA || '';
		const textB = typingElement.dataset.textB || '';
		let currentText = '';
		let isDeleting = false;
		let textIndex = 0; // 0 for textA, 1 for textB

		const typeText = (text, callback) => {
			let charIndex = 0;
			const timeline = gsap.timeline();

			timeline.to({}, {
				duration: text.length * 0.1,
				ease: "none",
				onUpdate: function() {
					const progress = this.progress();
					const targetIndex = Math.floor(progress * text.length);

					if (targetIndex > charIndex) {
						charIndex = targetIndex;
						currentText = text.substring(0, charIndex);
						typingElement.textContent = currentText;
					}
				},
				onComplete: callback
			});
		};

		const deleteText = (callback) => {
			const startLength = currentText.length;
			const timeline = gsap.timeline();

			timeline.to({}, {
				duration: startLength * 0.05,
				ease: "none",
				onUpdate: function() {
					const progress = this.progress();
					const targetLength = Math.floor(startLength * (1 - progress));
					currentText = currentText.substring(0, targetLength);
					typingElement.textContent = currentText;
				},
				onComplete: callback
			});
		};

		const animationLoop = () => {
			const currentFullText = textIndex === 0 ? textA : textB;

			// Type the current text
			typeText(currentFullText, () => {
				// Wait before deleting
				gsap.delayedCall(1.75, () => {
					// Delete the text
					deleteText(() => {
						// Wait before typing next text
						gsap.delayedCall(0.5, () => {
							// Switch to the other text
							textIndex = textIndex === 0 ? 1 : 0;
							// Continue the loop
							animationLoop();
						});
					});
				});
			});
		};

		// Start the animation loop
		animationLoop();
	}

	// Add black background to navbar based on scroll position
	const navbar = document.querySelector('#wrapper-navbar .navbar');

	if (navbar) {
		gsap.to(navbar, {
			backgroundColor: "rgba(0, 0, 0, 1)",
			padding: "1rem 0 1rem",
			scrollTrigger: {
				trigger: "body",
				start: "top top",
				end: "+=300",
				scrub: true,
				onUpdate: (self) => {
					const opacity = self.progress;
					navbar.style.backgroundColor = `rgba(0, 0, 0, ${opacity})`;
				}
			}
		});
	}

	// Word-by-word reveal animation for slice-title
	const sliceTitles = document.querySelectorAll('.slice-title');

	sliceTitles.forEach((title) => {
		// Split text into words
		const text = title.textContent;
		const words = text.split(' ');

		// Clear the original text and create word spans
		title.innerHTML = '';

		words.forEach((word, index) => {
			const wordWrapper = document.createElement('span');
			wordWrapper.style.display = 'inline-block';
			wordWrapper.style.overflow = 'hidden';
			wordWrapper.style.verticalAlign = 'top';
			wordWrapper.style.marginRight = '0.25em'; // Add space between words

			const wordSpan = document.createElement('span');
			wordSpan.textContent = word;
			wordSpan.style.display = 'inline-block';
			wordSpan.style.transform = 'translateY(100%)';
			wordSpan.style.opacity = '0';

			wordWrapper.appendChild(wordSpan);
			title.appendChild(wordWrapper);
		});

		// Animate each word
		const wordSpans = title.querySelectorAll('span span');

		gsap.to(wordSpans, {
			y: 0,
			opacity: 1,
			duration: 0.8,
			ease: "power3.out",
			stagger: 0.2,
			delay: 0.3
		});
	});

	// Features scroll animation
	const sectionFeatures = document.querySelector('.section-features');

	if (sectionFeatures) {
		const featuresWrapper = sectionFeatures.querySelector('.features-wrapper');

		if (featuresWrapper) {
			const featuresCount = parseInt(featuresWrapper.dataset.featuresCount);
			const featureImages = featuresWrapper.querySelectorAll('.feature-image');
			const featureItems = featuresWrapper.querySelectorAll('.feature-item');
			const featureTexts = featuresWrapper.querySelectorAll('.feature-text');
			const featureTitles = featuresWrapper.querySelectorAll('.feature-title');

			// Check if we're on mobile (768px breakpoint matches Bootstrap's md)
			const isMobile = () => window.innerWidth < 768;

			if (featuresCount > 0 && !isMobile()) {
				let currentActiveIndex = 0;

				// Function to switch to a specific feature
				const switchToFeature = (targetIndex, duration = 0.5) => {
					if (targetIndex === currentActiveIndex) return;

					// Update images and handle video playback
					featureImages.forEach((img, index) => {
						const video = img.querySelector('video');

						if (index === targetIndex) {
							gsap.to(img, { opacity: 1, duration: duration, ease: 'power2.out' });
							img.classList.add('active');

							// Play video when it becomes active
							if (video) {
								video.play().catch(err => console.log('Video play failed:', err));
							}
						} else {
							gsap.to(img, { opacity: 0, duration: duration, ease: 'power2.in' });
							img.classList.remove('active');

							// Pause video when it becomes inactive
							if (video) {
								video.pause();
							}
						}
					});

					// Update feature items
					featureItems.forEach((item, index) => {
						if (index === targetIndex) {
							item.classList.add('active');
						} else {
							item.classList.remove('active');
						}
					});

					// Update feature texts with smoother transitions
					featureTexts.forEach((text, index) => {
						if (index === targetIndex) {
							gsap.to(text, {
								opacity: 1,
								height: 'auto',
								duration: duration,
								ease: 'power2.out'
							});
						} else {
							gsap.to(text, {
								opacity: 0,
								height: 0,
								duration: duration * 0.7,
								ease: 'power2.in'
							});
						}
					});

					currentActiveIndex = targetIndex;
				};

				// Set initial states
				featureImages.forEach((img, index) => {
					const video = img.querySelector('video');

					gsap.set(img, { opacity: index === 0 ? 1 : 0 });
					if (index === 0) {
						img.classList.add('active');
						// Play the first video if it exists
						if (video) {
							video.play().catch(err => console.log('Video play failed:', err));
						}
					} else {
						// Pause all other videos
						if (video) {
							video.pause();
						}
					}
				});

				featureItems.forEach((item, index) => {
					if (index === 0) item.classList.add('active');
				});

				featureTexts.forEach((text, index) => {
					if (index === 0) {
						// Show the first feature text
						gsap.set(text, { opacity: 1, height: 'auto', overflow: 'hidden' });
					} else {
						gsap.set(text, { opacity: 0, height: 0, overflow: 'hidden' });
					}
				});

				// Add click handlers to titles
				featureTitles.forEach((title, index) => {
					title.addEventListener('click', () => {
						switchToFeature(index, 0.3);
					});
				});

				// Create scroll trigger for pinning
				ScrollTrigger.create({
					trigger: sectionFeatures,
					start: 'top top',
					end: () => `+=${featuresCount * 100}%`,
					pin: true,
					scrub: 1,
					onUpdate: (self) => {
						const progress = self.progress;
						const targetIndex = Math.min(
							Math.floor(progress * featuresCount),
							featuresCount - 1
						);
						const segmentProgress = (progress * featuresCount) % 1;

						// Update current index if it changed via scroll
						if (targetIndex !== currentActiveIndex && segmentProgress < 0.3) {
							switchToFeature(targetIndex, 0.3);
						}
					}
				});
			}
		}
	}

	// Horizontal Scrolling Galleries - Slide Right
	// Wait for images to load and CSS to apply before calculating
	setTimeout(() => {
		const galleriesRight = document.querySelectorAll('.scroll-gallery.gallery-slide-right');

		galleriesRight.forEach((gallery) => {
			const items = gallery.querySelectorAll('.scroll-gallery-item');

			if (items.length > 0) {
				// Use the container width
				const containerWidth = items[0].offsetWidth;
				const gap = 32; // Adjust this to control gap between images (in pixels)
				const imageWidth = containerWidth + gap;

				// Calculate total width needed to show 1.5 images initially
				const totalWidth = items.length * imageWidth;
				const visibleWidth = imageWidth * 1.5;
				const slideDistance = totalWidth - visibleWidth;

				// Set initial position to show 1.5 images from the RIGHT
				gsap.set(items, {
					x: (index) => {
						// Position images in a row, but offset to show rightmost 1.5 images
						return (index * imageWidth) - slideDistance;
					}
				});

				// Animate images sliding to the right on scroll
				ScrollTrigger.create({
					trigger: gallery,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 100,
					onUpdate: (self) => {
						const progress = self.progress;

						items.forEach((item, index) => {
							gsap.to(item, {
								x: (index * imageWidth) - (slideDistance * (1 - progress)),
								duration: 1,
								ease: 'none'
							});
						});
					}
				});
			}
		});
	}, 100);

	// Horizontal Scrolling Galleries - Slide Left
	// Wait for images to load and CSS to apply before calculating
	setTimeout(() => {
		const galleriesLeft = document.querySelectorAll('.scroll-gallery.gallery-slide-left');

		galleriesLeft.forEach((gallery) => {
			const items = gallery.querySelectorAll('.scroll-gallery-item');

			if (items.length > 0) {
				// Use the container width
				const containerWidth = items[0].offsetWidth;
				const gap = 32; // Adjust this to control gap between images (in pixels)
				const imageWidth = containerWidth + gap;

				// Calculate total width needed to show 1.5 images initially
				const totalWidth = items.length * imageWidth;
				const visibleWidth = imageWidth * 1.5;
				const slideDistance = totalWidth - visibleWidth;

				// Set initial position to show 1.5 images from the left
				gsap.set(items, {
					x: (index) => {
						// Position images in a row starting from the right
						return (index * imageWidth) + slideDistance;
					}
				});

				// Animate images sliding to the left on scroll
				ScrollTrigger.create({
					trigger: gallery,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 3,
					onUpdate: (self) => {
						const progress = self.progress;

						items.forEach((item, index) => {
							gsap.to(item, {
								x: (index * imageWidth) + (slideDistance * (1 - progress)),
								duration: 0.1,
								ease: 'none'
							});
						});
					}
				});
			}
		});
	}, 100);

	// Horizontal Scrolling Testimonials - Auto-scroll + Draggable/Swipeable
	// Wait for images to load and CSS to apply before calculating
	setTimeout(() => {
		const testimonials = document.querySelectorAll('.scroll-testimonials');

		testimonials.forEach((testimonialSection) => {
			const items = testimonialSection.querySelectorAll('.scroll-testimonial-item');

			if (items.length > 0) {
				// Use the container width
				const containerWidth = items[0].offsetWidth;
				const gap = 32; // Larger gap between testimonials
				const itemWidth = containerWidth + gap;

				// Calculate total width needed to show 2.5 testimonials initially
				const totalWidth = items.length * itemWidth;
				const visibleWidth = itemWidth * 2.5;
				const slideDistance = totalWidth - visibleWidth;
				const sectionWidth = testimonialSection.offsetWidth;
				const maxDragDistance = totalWidth - sectionWidth;

				// Set initial position to show 2.5 testimonials from the LEFT
				gsap.set(items, {
					x: (index) => {
						// Position testimonials starting further left (negative offset)
						return (index * itemWidth) - (itemWidth * 0.5);
					}
				});

				let isDragging = false;
				let accumulatedDragOffset = 0; // Total accumulated drag distance

				// Function to update positions
				const updatePositions = (scrollProgress, currentDragDelta = 0) => {
					items.forEach((item, index) => {
						const scrollBasedX = (index * itemWidth) - (itemWidth * .75) - (slideDistance * scrollProgress);
						const finalX = scrollBasedX + accumulatedDragOffset + currentDragDelta;
						gsap.set(item, { x: finalX });
					});
				};

				// Create ScrollTrigger for auto-scroll when moving down page
				const scrollTriggerInstance = ScrollTrigger.create({
					trigger: testimonialSection,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 15, // Slower scrub value = slower movement
					onUpdate: (self) => {
						if (!isDragging) {
							updatePositions(self.progress, 0);
						}
					}
				});

				// Create a wrapper for draggable
				const draggableProxy = document.createElement('div');
				draggableProxy.style.position = 'absolute';
				draggableProxy.style.width = '100%';
				draggableProxy.style.height = '100%';
				draggableProxy.style.top = '0';
				draggableProxy.style.left = '0';
				draggableProxy.style.pointerEvents = 'none';
				testimonialSection.style.position = 'relative';
				testimonialSection.appendChild(draggableProxy);

				// Make it draggable
				const draggable = Draggable.create(draggableProxy, {
					type: 'x',
					trigger: testimonialSection,
					onDragStart: function() {
						isDragging = true;
					},
					onDrag: function() {
						const scrollProgress = scrollTriggerInstance.progress || 0;
						const currentDragDelta = this.x;
						updatePositions(scrollProgress, currentDragDelta);
					},
					onDragEnd: function() {
						isDragging = false;

						// Add this drag to accumulated offset
						accumulatedDragOffset += this.x;

						// Get current scroll progress
						const scrollProgress = scrollTriggerInstance.progress || 0;
						const scrollBasedX = -(slideDistance * scrollProgress);

						// Calculate where the first item will be
						const firstItemBaseX = -(itemWidth * .75) + scrollBasedX;
						const firstItemFinalX = firstItemBaseX + accumulatedDragOffset;

						// Calculate where the last item will be
						const lastItemBaseX = ((items.length - 1) * itemWidth) - (itemWidth * .75) + scrollBasedX;
						const lastItemFinalX = lastItemBaseX + accumulatedDragOffset;

						// Allow dragging right until first item is at itemWidth * 0.75 (fully visible)
						const maxAllowedX = (itemWidth * 0.75) - firstItemBaseX;
						// Allow dragging left until last item right edge is at screen right edge
						const minAllowedX = (sectionWidth - itemWidth) - lastItemBaseX;

						if (accumulatedDragOffset > maxAllowedX) {
							// Too far right, clamp it
							accumulatedDragOffset = maxAllowedX;
						} else if (accumulatedDragOffset < minAllowedX) {
							// Too far left, clamp it
							accumulatedDragOffset = minAllowedX;
						}

						// Reset proxy for next drag
						gsap.set(draggableProxy, { x: 0 });

						// Update positions with clamped offset
						updatePositions(scrollProgress, 0);
					}
				})[0];

				// Add cursor styling
				testimonialSection.style.cursor = 'grab';
				testimonialSection.addEventListener('mousedown', () => {
					testimonialSection.style.cursor = 'grabbing';
				});
				testimonialSection.addEventListener('mouseup', () => {
					testimonialSection.style.cursor = 'grab';
				});

				// Prevent text selection while dragging
				testimonialSection.style.userSelect = 'none';
				testimonialSection.style.webkitUserSelect = 'none';
			}
		});
	}, 100);

	// Phone Loop - Auto-scroll + Draggable/Swipeable (Mobile Only)
	// Wait for images to load and CSS to apply before calculating
	setTimeout(() => {
		// Only run on mobile
		const isMobile = () => window.innerWidth < 768;

		if (!isMobile()) return;

		const phoneLoops = document.querySelectorAll('.phone-loop-wrapper');

		phoneLoops.forEach((phoneLoopSection) => {
			const items = phoneLoopSection.querySelectorAll('.phone-loop-item');

			if (items.length > 0) {
				// Use the container width
				const containerWidth = items[0].offsetWidth;
				const gap = 24; // Gap between items (1.5rem = 24px)
				const itemWidth = containerWidth + gap;

				// Calculate bounds
				const totalWidth = items.length * itemWidth;
				const sectionWidth = phoneLoopSection.offsetWidth;
				const maxDragDistance = totalWidth - sectionWidth;
				const slideDistance = maxDragDistance;

				// Set initial position
				gsap.set(items, {
					x: (index) => {
						return index * itemWidth;
					}
				});

				let isDragging = false;
				let accumulatedDragOffset = 0;

				// Function to update positions
				const updatePositions = (scrollProgress, currentDragDelta = 0) => {
					items.forEach((item, index) => {
						const scrollBasedX = (index * itemWidth) - (slideDistance * scrollProgress);
						const finalX = scrollBasedX + accumulatedDragOffset + currentDragDelta;
						gsap.set(item, { x: finalX });
					});
				};

				// Create ScrollTrigger for auto-scroll when moving down page
				const scrollTriggerInstance = ScrollTrigger.create({
					trigger: phoneLoopSection,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 10,
					onUpdate: (self) => {
						if (!isDragging) {
							updatePositions(self.progress, 0);
						}
					}
				});

				// Create a wrapper for draggable
				const draggableProxy = document.createElement('div');
				draggableProxy.style.position = 'absolute';
				draggableProxy.style.width = '100%';
				draggableProxy.style.height = '100%';
				draggableProxy.style.top = '0';
				draggableProxy.style.left = '0';
				draggableProxy.style.pointerEvents = 'none';
				phoneLoopSection.style.position = 'relative';
				phoneLoopSection.appendChild(draggableProxy);

				// Make it draggable
				const draggable = Draggable.create(draggableProxy, {
					type: 'x',
					trigger: phoneLoopSection,
					onDragStart: function() {
						isDragging = true;
					},
					onDrag: function() {
						const scrollProgress = scrollTriggerInstance.progress || 0;
						const currentDragDelta = this.x;
						updatePositions(scrollProgress, currentDragDelta);
					},
					onDragEnd: function() {
						isDragging = false;

						// Add this drag to accumulated offset
						accumulatedDragOffset += this.x;

						// Get current scroll progress
						const scrollProgress = scrollTriggerInstance.progress || 0;
						const scrollBasedX = -(slideDistance * scrollProgress);

						// Calculate where the first item will be
						const firstItemBaseX = 0 + scrollBasedX;
						const firstItemFinalX = firstItemBaseX + accumulatedDragOffset;

						// Calculate where the last item will be
						const lastItemBaseX = ((items.length - 1) * itemWidth) + scrollBasedX;
						const lastItemFinalX = lastItemBaseX + accumulatedDragOffset;

						// Allow dragging right until first item is at position 0 (left edge)
						const maxAllowedX = 0 - firstItemBaseX;
						// Allow dragging left until last item right edge is at screen right edge
						const minAllowedX = (sectionWidth - containerWidth) - lastItemBaseX;

						if (accumulatedDragOffset > maxAllowedX) {
							// Too far right, clamp it
							accumulatedDragOffset = maxAllowedX;
						} else if (accumulatedDragOffset < minAllowedX) {
							// Too far left, clamp it
							accumulatedDragOffset = minAllowedX;
						}

						// Reset proxy for next drag
						gsap.set(draggableProxy, { x: 0 });

						// Update positions with clamped offset
						updatePositions(scrollProgress, 0);
					}
				})[0];

				// Prevent text selection while dragging
				phoneLoopSection.style.userSelect = 'none';
				phoneLoopSection.style.webkitUserSelect = 'none';
			}
		});
	}, 100);

	// Number count up animation with comma formatting
	const overviewNumbers = document.querySelectorAll('.overview-number span');

	overviewNumbers.forEach((element) => {
		const targetNumber = parseFloat(element.textContent.replace(/,/g, ''));

		// Set initial value to 0
		element.textContent = '0';

		// Create the count up animation
		ScrollTrigger.create({
			trigger: element,
			start: 'top 85%',
			onEnter: () => {
				gsap.to({ value: 0 }, {
					value: targetNumber,
					duration: 2,
					ease: 'power1.out',
					onUpdate: function() {
						const currentValue = Math.floor(this.targets()[0].value);
						// Format with commas for thousands
						element.textContent = currentValue.toLocaleString();
					},
					onComplete: function() {
						// Ensure final value is exact
						element.textContent = targetNumber.toLocaleString();
					}
				});
			},
			once: true
		});
	});

	// Reusable fade-in-up animation class with stagger
	const fadeInUpElements = document.querySelectorAll('.fade-in-up');

	if (fadeInUpElements.length > 0) {
		// Set initial state
		gsap.set(fadeInUpElements, { y: 50, opacity: 0 });

		// Animate all elements with stagger effect
		gsap.to(fadeInUpElements, {
			y: 0,
			opacity: 1,
			duration: 1,
			ease: "power2.out",
			stagger: 0.2,
			scrollTrigger: {
				trigger: fadeInUpElements[0],
				start: "top 85%",
				end: "top 20%",
				toggleActions: "play none none reverse",
			}
		});
	}

	// Parallax effect for elements with .parallax class
	const parallaxElements = document.querySelectorAll('.parallax');
	const isMobileView = () => window.innerWidth < 768;

	parallaxElements.forEach((element) => {
		const speed = element.dataset.speed || 0.5;
		const initialY = 200 * speed; // Start position (opposite of end position)

		// Skip parallax on mobile to prevent layout shift issues
		if (isMobileView()) {
			return;
		}

		// Set initial position immediately to prevent layout shift
		gsap.set(element, { y: initialY });

		gsap.to(element, {
			y: () => -200 * speed,
			ease: "none",
			scrollTrigger: {
				trigger: element,
				start: "top bottom",
				end: "bottom top",
				scrub: 1,
				invalidateOnRefresh: true
			}
		});
	});

	// Ani-under GIF - Play when in view, replay on re-entry
	const aniUnderElements = document.querySelectorAll('.ani-under');

	aniUnderElements.forEach((element) => {
		// Wrap existing content in a container with higher z-index
		const contentWrapper = document.createElement('span');
		contentWrapper.style.position = 'relative';
		contentWrapper.style.zIndex = '2';

		// Move all child nodes into the content wrapper
		while (element.firstChild) {
			contentWrapper.appendChild(element.firstChild);
		}

		// Create wrapper for both gif and static image
		const mediaWrapper = document.createElement('div');
		mediaWrapper.className = 'ani-under-media';
		mediaWrapper.style.position = 'absolute';
		mediaWrapper.style.bottom = '0';
		mediaWrapper.style.left = '0';
		mediaWrapper.style.width = '100%';
		mediaWrapper.style.pointerEvents = 'none';
		mediaWrapper.style.zIndex = '1';

		// Create the animated gif element
		const gif = document.createElement('img');
		gif.className = 'ani-under-gif';
		gif.style.width = '100%';
		gif.style.height = 'auto';
		gif.style.display = 'none';
		gif.style.objectFit = 'contain';

		// Create the static image element (for the last frame)
		const staticImg = document.createElement('img');
		staticImg.className = 'ani-under-static';
		staticImg.style.width = '100%';
		staticImg.style.height = 'auto';
		staticImg.style.display = 'none';
		staticImg.style.objectFit = 'contain';

		mediaWrapper.appendChild(gif);
		mediaWrapper.appendChild(staticImg);

		// Ensure parent has position relative and proper display for inline elements
		const computedStyle = getComputedStyle(element);
		if (computedStyle.position === 'static') {
			element.style.position = 'relative';
		}
		// If it's an inline element (like span), make it inline-block
		if (computedStyle.display === 'inline') {
			element.style.display = 'inline-block';
		}

		// Add both to the element
		element.appendChild(mediaWrapper);
		element.appendChild(contentWrapper);

		// Intersection Observer
		const observerOptions = {
			threshold: 0.3,
			rootMargin: '0px'
		};

		let animationTimeout = null;
		const gifUrl = element.dataset.gifUrl || '/wp-content/themes/cream-theme/assets/images/ani-under.gif';
		const staticUrl = element.dataset.staticUrl || gifUrl.replace('.gif', '-static.png');
		const duration = parseInt(element.dataset.gifDuration) || 1640;

		// Preload the static image so it's ready instantly
		staticImg.src = staticUrl;

		const observerCallback = (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					// Clear any existing timeout
					if (animationTimeout) {
						clearTimeout(animationTimeout);
					}

					// Show gif, hide static
					staticImg.style.display = 'none';
					gif.style.display = 'block';

					// Load the gif with cache buster to restart animation
					gif.src = gifUrl + '?t=' + Date.now();

					// After duration, switch to static image (subtract a bit to catch before loop)
					animationTimeout = setTimeout(() => {
						gif.style.display = 'none';
						staticImg.style.display = 'block';
					}, duration - 100);
				} else {
					// Out of view - reset
					if (animationTimeout) {
						clearTimeout(animationTimeout);
						animationTimeout = null;
					}
					gif.src = '';
					gif.style.display = 'none';
					staticImg.style.display = 'none';
				}
			});
		};

		const observer = new IntersectionObserver(observerCallback, observerOptions);
		observer.observe(element);
	});

	// Video Player - Autoplay on view with mute/unmute control
	const videoPlayers = document.querySelectorAll('[data-video-player]');

	videoPlayers.forEach((player) => {
		const video = player.querySelector('.video-player-video');
		const muteButton = player.querySelector('.video-player-mute-button');

		if (!video || !muteButton) return;

		// Video starts muted for iOS compatibility
		// User can unmute by clicking the button

		// Update button appearance based on muted state
		const updateMuteButton = () => {
			const iconMuted = muteButton.querySelector('.icon-muted');
			const iconUnmuted = muteButton.querySelector('.icon-unmuted');

			if (video.muted) {
				iconMuted.style.display = 'block';
				iconUnmuted.style.display = 'none';
				muteButton.setAttribute('aria-label', 'Unmute video');
			} else {
				iconMuted.style.display = 'none';
				iconUnmuted.style.display = 'block';
				muteButton.setAttribute('aria-label', 'Mute video');
			}
		};

		// Initialize button appearance
		updateMuteButton();

		// Mute/unmute toggle
		muteButton.addEventListener('click', () => {
			video.muted = !video.muted;
			updateMuteButton();
		});

		// The main autoplay handler should handle this, but let's add extra triggers
		// Intersection Observer specifically for this Video Player
		const videoPlayerObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					if (video.paused) {
						video.muted = true;
						video.load();
						video.play().catch((error) => {
							console.log('Video Player autoplay failed:', error);
						});
					}
				}
			});
		}, {
			threshold: 0.25,
			rootMargin: '50px'
		});

		videoPlayerObserver.observe(player);

		// Also try on user interactions
		const tryPlay = () => {
			if (video.paused) {
				video.muted = true;
				video.play().catch(err => console.log('Video Player play attempt failed:', err));
			}
		};

		document.addEventListener('touchstart', tryPlay, { once: true, passive: true });
		document.addEventListener('scroll', tryPlay, { once: true, passive: true });
		document.addEventListener('click', tryPlay, { once: true });

		// Try immediately after a delay
		setTimeout(tryPlay, 500);
	});

	// Close offcanvas navigation when a link is clicked
	const offcanvasElement = document.getElementById('navbarOffcanvas');
	if (offcanvasElement) {
		// Get all links inside the offcanvas
		const offcanvasLinks = offcanvasElement.querySelectorAll('a');

		offcanvasLinks.forEach(link => {
			link.addEventListener('click', () => {
				// Method 1: Find and click the close button
				const closeButton = offcanvasElement.querySelector('.btn-close');
				if (closeButton) {
					closeButton.click();
				}

				// Method 2: Try Bootstrap API if available
				if (typeof bootstrap !== 'undefined' && bootstrap.Offcanvas) {
					try {
						const bsOffcanvas = bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
						bsOffcanvas.hide();
					} catch (err) {
						console.error('Bootstrap API failed:', err);
					}
				}

				// Method 3: Remove Bootstrap classes manually as fallback
				setTimeout(() => {
					if (offcanvasElement.classList.contains('show')) {
						offcanvasElement.classList.remove('show');
						document.body.classList.remove('offcanvas-open');
						const backdrop = document.querySelector('.offcanvas-backdrop');
						if (backdrop) {
							backdrop.remove();
						}
					}
				}, 100);
			});
		});
	}

});

export default function initInitGsap() {
    gsap.registerPlugin(ScrollTrigger, Draggable);
}

